<template>
	<templates-limit-exceeded v-if="reachedCampaignLimit" />
	<core-page v-else title="Select a Campaign Type:" classes="templates-wrapper">
		<common-alerts :alerts="alerts" />
		<div class="templates-content">
			<templates-types />
			<templates-filters />

			<div class="omapi-content-area">
				<templates-upsell-alerts />

				<div v-if="hasTemplates" class="omapi-template-listing-wrap">
					<templates-grid :templates="selectedTemplates" />
					<svg-loading v-if="isLoading" :style="{ margin: '0 auto' }"></svg-loading>
				</div>

				<div v-else-if="isLoading" class="archie-loader-wrapper">
					<div><core-loading class="loader" /></div>
				</div>

				<div v-else class="no-templates-available">
					<core-alert :type="validType ? 'info' : 'warn'">
						<div class="alert-message">
							{{ noTemplatesMessage }}
						</div>
					</core-alert>
				</div>
			</div>
		</div>
		<campaigns-modal-create-campaign />
		<templates-modal-not-connected />
		<templates-modal-no-access />
	</core-page>
</template>

<script lang="js">
import get from 'lodash/get'
import orderBy from 'lodash/orderBy'
import upperFirst from 'lodash/upperFirst'

import {
	closeCampaigns,
	maybeMobiilePreview,
	maybeEndMobiilePreview,
	setDemo,
	setPreview,
	enableBgClick,
} from '@/utils/om-api'
import { createNamespacedHelpers } from 'vuex'
const { mapState, mapActions, mapGetters } = createNamespacedHelpers('templates')

export default  {
	computed : {
		...mapState([
			'activeType',
			'search',
			'sort',
			'popular',
			'templates',
		]),
		...mapGetters([ 'typePermitted', 'featured', 'filters', 'filterGamified', 'validType' ]),
		alerts () {
			return this.$get('$store.state.alerts', [])
		},
		isLoading () {
			return this.$store.getters.isLoading('templates')
		},
		popularTemplates () {
			const popular = this.popular[this.activeType]

			return popular && popular.length ? this.order(popular) : []
		},
		featuredTemplates () {
			const featured = this.featured[this.activeType]

			return featured && featured.length ? this.order(featured) : []
		},
		showableTemplates () {
			const filterPasses = (key, value) => {
				return this.filters[key].length
					? !this.filters[key].find(f => !value.includes(f))
					: true
			}

			const prop = [ 'popular', 'featured' ].includes(this.sort) ? `${this.sort}Templates` : 'templates'
			const templates = this[ prop ]
				.filter((template) => {
					if ('' === this.search) {
						return true
					}
					return template.name.toLowerCase().includes(this.search.toLowerCase())
				}).filter((template) => {
					const device = 'mobile' === this.filters.device
						? template.mobile
						: !template.mobile

					if (!device) {
						return false
					}

					const taxonomies = [ 'goals', 'categories', 'tags' ]

					return !taxonomies.find(t => {
						return !filterPasses(t, template[t].map(term => term.id))
					})
				})

			return this.order(templates)
		},
		selectedTemplates () {
			return this.showableTemplates.filter(template => {
				return this.filterGamified
					? template.tags.find(tag => 1 === tag.id)
					: template.type === this.activeType
			})
		},
		hasTemplates () {
			return this.selectedTemplates.length
		},
		shouldShowUpsells () {
			return this.$store.getters.connected
				? !this.typePermitted(this.activeType)
				: false
		},
		noTemplatesMessage () {
			return this.validType
				? 'No templates available for your current selection.'
				: `${upperFirst(this.activeType)} is not a valid type. Please select one of the options above.`
		},
		reachedCampaignLimit () {
			if (!this.$store.getters.connected) {
				return false
			}
			const total = this.$get('$store.state.campaigns.totalCampaignsCount', 0)
			const limit = get(this.$store.getters.userAttribute('limits', {}), 'campaigns', 10)

			return total > limit
		},
	},
	mounted () {
		this.fetchTemplateData().catch(() => {})
		this.$bus.$emit('dashboard-view-mounted', 'templates')
	},
	created () {
		this.listenApiLoaded()

		// Ensure campaigns are flagged as demos.
		document.addEventListener('om.Main.getCampaigns', setDemo)

		// Make previews closeable by clicking background.
		document.addEventListener('om.Campaigns.init', enableBgClick)

		// Disable form fields
		document.addEventListener('om.Optin.init', setPreview)

		// Handle mobile template previews
		document.addEventListener('om.Campaign.afterShow', maybeMobiilePreview)
		document.addEventListener('om.Campaign.startClose', maybeEndMobiilePreview)

		this.$store.subscribe((mutation) => {
			const ignore = [
				'templates/setLoadingPreview',
				'templates/setPreviewing',
			]
			if (-1 === ignore.indexOf(mutation.type)) {
				closeCampaigns()
				this.$store.commit('templates/setLoadingPreview', '')
				this.$store.commit('templates/setPreviewing', '')
			}
		})

		this.$store.dispatch('campaigns/fetchDashboard').catch(() => {})

		this.loadApi()
	},
	beforeDestroy () {
		// Ensure campaigns are flagged as demos.
		document.removeEventListener('om.Main.getCampaigns', setDemo)

		// Make previews closeable by clicking background.
		document.removeEventListener('om.Campaigns.init', enableBgClick)

		// Disable form fields
		document.removeEventListener('om.Optin.init', setPreview)

		// Handle mobile template previews
		document.removeEventListener('om.Campaign.afterShow', maybeMobiilePreview)
		document.removeEventListener('om.Campaign.startClose', maybeEndMobiilePreview)
	},
	methods : {
		...mapActions([ 'fetchTemplateData' ]),
		listenApiLoaded (cb = 'addEventListener') {
			[
				'om.Api.init',
				'om.Main.init',
				'om.Campaigns.init',
				'om.Campaign.init',
			].forEach(evt => document[cb](evt, this.setApiLoaded))
		},
		loadApi () {
			let script = document.getElementById('omwpapi-templates-apijs')
			if (script) {
				return
			}
			const head   = document.getElementsByTagName('head')[0] || document.documentElement
			script = document.createElement('script')
			script.type            = 'text/javascript'
			script.id              = 'omwpapi-templates-apijs'
			script.src             = this.$urls.apiJs()
			script.async           = true
			script.dataset.account = 56690
			script.dataset.user    = 50374
			if (!this.$env.isProduction()) {
				script.dataset.env = this.$env.isDevelopment() ? 'dev' : this.$env.currentEnv
			}

			head.appendChild(script)
		},
		setApiLoaded () {
			this.listenApiLoaded('removeEventListener')
			this.$store.commit('templates/apiLoaded')
		},
		applyFilters (filters) {
			this.$bus.$emit('applied-bulk-filter')
			this.$store.commit('templates/appliedFilters', filters)
		},
		order (templates) {
			return orderBy(templates, (template) => {
				return 'recent' === this.sort || 'featured' === this.sort ? -this.$moment(template.created_at).valueOf() : template.name
			})
		},
	},

};
</script>
<style lang="scss">
@import '../assets/scss/variables';
@import '../assets/scss/pages/templates';
</style>
